import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { PrivateMediaImage } from "./PrivateMediaImage";

export interface MomentMediaGalleryItem {
  id: string;
  path: string;
  alt: string;
}

export function MomentMediaGallery({
  items,
  compact = false,
  className = "",
}: {
  items: MomentMediaGalleryItem[];
  compact?: boolean;
  className?: string;
}) {
  const normalized = useMemo(() => items.filter(item => item.id && item.path), [items]);
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (index >= normalized.length) setIndex(Math.max(0, normalized.length - 1));
  }, [index, normalized.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxOpen(false);
      if (event.key === "ArrowLeft") previous();
      if (event.key === "ArrowRight") next();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxOpen, normalized.length]);

  if (normalized.length === 0) return null;

  const current = normalized[index] ?? normalized[0];
  const multiple = normalized.length > 1;

  function previous() {
    if (normalized.length < 2) return;
    setIndex(value => (value - 1 + normalized.length) % normalized.length);
  }

  function next() {
    if (normalized.length < 2) return;
    setIndex(value => (value + 1) % normalized.length);
  }

  function onTouchStart(event: TouchEvent) {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  }

  function onTouchEnd(event: TouchEvent) {
    const start = touchStartX.current;
    const end = event.changedTouches[0]?.clientX;
    touchStartX.current = null;
    if (start == null || end == null || Math.abs(start - end) < 45) return;
    if (end < start) next();
    else previous();
  }

  const stageHeight = compact ? "h-52" : "h-[22rem] sm:h-[26rem]";

  return <>
    <div className={`relative overflow-hidden rounded-[1.25rem] border border-white/8 bg-black/30 ${className}`}>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={`group relative grid w-full place-items-center ${stageHeight}`}
        aria-label={`Ampliar foto ${index + 1} de ${normalized.length}`}
      >
        <PrivateMediaImage
          path={current.path}
          alt={current.alt}
          className="h-full w-full object-contain object-center"
        />
        <span className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-black/55 text-white/80 backdrop-blur-sm transition group-hover:bg-black/70">
          <Maximize2 className="h-4 w-4" />
        </span>
      </button>

      {multiple && <>
        <button type="button" onClick={previous} className="absolute left-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-white/10 bg-black/55 text-white backdrop-blur-sm" aria-label="Foto anterior"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" onClick={next} className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-white/10 bg-black/55 text-white backdrop-blur-sm" aria-label="Próxima foto"><ChevronRight className="h-5 w-5" /></button>
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-black/60 px-2.5 py-1 text-[10px] font-bold text-white/85 backdrop-blur-sm">{index + 1} / {normalized.length}</span>
      </>}
    </div>

    {lightboxOpen && typeof document !== "undefined" && createPortal(
      <div role="dialog" aria-modal="true" aria-label="Visualização ampliada do momento" className="fixed inset-0 z-[120] flex flex-col bg-black/95 text-white">
        <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))]">
          <span className="text-xs font-semibold text-white/65">{index + 1} de {normalized.length}</span>
          <button type="button" onClick={() => setLightboxOpen(false)} className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[.06]" aria-label="Fechar foto"><X className="h-5 w-5" /></button>
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <PrivateMediaImage path={current.path} alt={current.alt} className="h-full w-full object-contain object-center" />
          {multiple && <>
            <button type="button" onClick={previous} className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/10 bg-black/55" aria-label="Foto anterior"><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" onClick={next} className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/10 bg-black/55" aria-label="Próxima foto"><ChevronRight className="h-6 w-6" /></button>
          </>}
        </div>
      </div>,
      document.body,
    )}
  </>;
}
